/**
 * Photos off a phone arrive as 4 to 12 MB HEIC files. The avatars bucket
 * wants something small and ordinary. This draws the picked file onto a
 * canvas, capped at maxSide, and hands back a JPEG File. Safari decodes
 * HEIC natively so the same path covers it. If anything in here fails
 * the original file is returned and the upload gets its own chance.
 */
export async function toJpeg(file, maxSide = 1024, quality = 0.86) {
  if (!file || typeof document === "undefined") return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("decode failed"));
      i.src = url;
    });
    const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
    if (!w || !h) return file;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = cw; canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, cw, ch);
    const blob = await new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", quality));
    if (!blob) return file;
    const base = (file.name || "photo").replace(/\.[^.]+$/, "") || "photo";
    return new File([blob], `${base}.jpg`, { type: "image/jpeg" });
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// "Miami, United States, United States" came from two profile screens
// disagreeing about whether city holds the country too. First distinct
// part is the city, last distinct part is the country.
export function splitLocation(str) {
  const seen = new Set();
  const parts = String(str || "").split(",").map(s => s.trim()).filter(Boolean)
    .filter(p => { const k = p.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
  if (!parts.length) return { city: null, country: null };
  if (parts.length === 1) return { city: parts[0], country: null };
  return { city: parts[0], country: parts[parts.length - 1] };
}

export function joinLocation(city, country) {
  const { city: c, country: k } = splitLocation([city, country].filter(Boolean).join(", "));
  return [c, k].filter(Boolean).join(", ");
}
